import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import DynamicForm from './DynamicForm';

const emptyPerson = () => ({
  firstName: '', lastName1: '', lastName2: '', birthYear: '',
  sex: '', email: '', phoneExt: '+34', phone: '',
  tutorName: '', address: '', municipality: '', province: '',
  country: 'España', docType: '', docNum: '', docSupport: '', docEmission: '',
  // Salud
  alergias: '', medicacionActual: '', enfermedades: '',
  grupoSanguineo: '', tarjetaSanitaria: '',
  contactoEmergenciaNombre: '', contactoEmergenciaTelefono: '',
  contactoEmergenciaExt: '+34', contactoEmergenciaRelacion: '',
  autorizaMedicacion: false,
  // Autorizaciones RGPD
  autorizaCesionDatos: false,
  autorizaImagenRedes: false,
  autorizaComunicaciones: false,
  // Referencia persona (Phase 2)
  _personaId: null,
});

/**
 * Convierte datos de una persona guardada al formato de miembro de booking.
 */
function personaToMember(persona) {
  const birthYear = persona.fechaNacimiento
    ? new Date(persona.fechaNacimiento).getFullYear()
    : '';
  return {
    firstName: persona.firstName || '',
    lastName1: persona.lastName1 || '',
    lastName2: persona.lastName2 || '',
    birthYear: birthYear || '',
    sex: persona.sex || '',
    email: persona.email || '',
    phoneExt: persona.phoneExt || '+34',
    phone: persona.phone || '',
    tutorName: '',
    address: persona.address || '',
    municipality: persona.municipality || '',
    province: persona.province || '',
    country: persona.country || 'España',
    docType: (persona.docType || '').toLowerCase(),
    docNum: persona.docNum || '',
    docSupport: persona.docSupport || '',
    docEmission: persona.docEmission || '',
    // Salud
    alergias: persona.alergias || '',
    medicacionActual: persona.medicacionActual || '',
    enfermedades: persona.enfermedades || '',
    grupoSanguineo: persona.grupoSanguineo || '',
    tarjetaSanitaria: persona.tarjetaSanitaria || '',
    contactoEmergenciaNombre: persona.contactoEmergenciaNombre || '',
    contactoEmergenciaTelefono: persona.contactoEmergenciaTelefono || '',
    contactoEmergenciaExt: persona.contactoEmergenciaExt || '+34',
    contactoEmergenciaRelacion: persona.contactoEmergenciaRelacion || '',
    autorizaMedicacion: persona.autorizaMedicacion || false,
    autorizaCesionDatos: persona.autorizaCesionDatos || false,
    autorizaImagenRedes: persona.autorizaImagenRedes || false,
    autorizaComunicaciones: persona.autorizaComunicaciones || false,
    // Referencia
    _personaId: persona.id,
  };
}

// ── Selector de persona guardada ────────────────────────────
function PersonaSelector({ personas, selectedId, onSelect }) {
  if (!personas || personas.length === 0) return null;
  return (
    <div style={{
      marginBottom: '.8rem', padding: '.7rem .9rem',
      background: 'rgba(74,143,168,.08)', border: '1px solid rgba(74,143,168,.2)',
      borderRadius: 'var(--radius)',
    }}>
      <div style={{ fontSize: '.7rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: '#4a8fa8', marginBottom: '.4rem' }}>
        Rellenar desde persona guardada
      </div>
      <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
        {personas.map(p => {
          const isSelected = selectedId === p.id;
          const age = p.edad ?? null;
          const label = p.nombre || `${p.firstName || ''} ${p.lastName1 || ''}`.trim();
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : p.id)}
              style={{
                padding: '.3rem .7rem', fontSize: '.78rem',
                border: `1px solid ${isSelected ? 'var(--sage)' : 'rgba(122,171,138,.25)'}`,
                borderRadius: '99px', cursor: 'pointer',
                background: isSelected ? 'rgba(122,171,138,.2)' : 'rgba(255,255,255,.05)',
                color: isSelected ? 'var(--sage)' : 'var(--white)',
                transition: 'all .15s',
              }}
            >
              {isSelected && '✓ '}{label}
              {age !== null && <span style={{ color: 'var(--mid)', marginLeft: '.3rem', fontSize: '.7rem' }}>({age}a)</span>}
            </button>
          );
        })}
      </div>
      {selectedId && (
        <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.3rem', fontStyle: 'italic' }}>
          Datos cargados — puedes modificarlos para esta inscripcion
        </div>
      )}
    </div>
  );
}

// ── Selector de opcion de pago ───────────────────────────────
function PaymentSelector({ event, selectedPlan, onSelect, members }) {
  const depositPct = parseFloat(event.depositPercent || 0);
  const hasNewFormat = Array.isArray(event.paymentOptions) && event.paymentOptions.length > 0;

  const calcTotal = () => {
    if (!event.useAgePricing || !event.agePricing) {
      return (event.pricePerPerson || 0) * (members?.length || 1);
    }
    return (members || []).reduce((sum, m) => {
      const birthYear = parseInt(m.birthYear);
      if (!birthYear) return sum + (parseFloat(event.agePricing.adult?.price) || 0);
      const age = new Date().getFullYear() - birthYear;
      if (age <= 3) return sum;
      if (age <= 15) return sum + (parseFloat(event.agePricing.child?.price) || 0);
      return sum + (parseFloat(event.agePricing.adult?.price) || 0);
    }, 0);
  };

  const totalAmount = calcTotal();
  const numMembers = members?.length || 1;

  // Calculate deposit amount — use event.depositAmount if set, else calculate from percent
  const calcDepositPerPerson = () => {
    if (event.depositAmount) return parseFloat(event.depositAmount);
    const perPerson = event.pricePerPerson || 0;
    return Math.round(perPerson * depositPct / 100 * 100) / 100;
  };
  const depositPerPerson = depositPct > 0 ? calcDepositPerPerson() : 0;
  const depositTotal = depositPerPerson * numMembers;

  const now = new Date();

  // ── NEW FORMAT: event has paymentOptions ──
  if (hasNewFormat) {
    const singleDueDate = event.singlePaymentDueDate ? new Date(event.singlePaymentDueDate) : null;
    const singleExpired = singleDueDate && singleDueDate < now;

    return (
      <div style={{ marginBottom: '1rem' }}>
        <div className="section-label" style={{ marginBottom: '.8rem' }}>Selecciona como quieres pagar</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          {/* Pago unico — always available */}
          <button
            onClick={() => !singleExpired && onSelect('single')}
            disabled={singleExpired}
            style={{
              width: '100%', textAlign: 'left', padding: '1rem 1.1rem',
              border: `2px solid ${singleExpired ? 'rgba(214,60,42,.3)' : selectedPlan === 'single' ? 'var(--sage)' : 'rgba(122,171,138,.2)'}`,
              borderRadius: 'var(--radius)',
              background: singleExpired ? 'rgba(214,60,42,.06)' : selectedPlan === 'single' ? 'rgba(122,171,138,.12)' : 'rgba(255,255,255,.03)',
              cursor: singleExpired ? 'not-allowed' : 'pointer',
              opacity: singleExpired ? 0.75 : 1,
              transition: 'all .2s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>Pago unico</div>
                <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.2rem' }}>Paga el importe completo ahora</div>
                {singleDueDate && !singleExpired && (
                  <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                    Disponible hasta el {singleDueDate.toLocaleDateString('es-ES')}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.2rem', fontWeight: 700, color: selectedPlan === 'single' ? 'var(--sage)' : 'var(--white)' }}>
                  {totalAmount.toFixed(2)} EUR
                </div>
                <div style={{ fontSize: '.65rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Total</div>
              </div>
            </div>
            {singleExpired && (
              <div style={{ marginTop: '.6rem', fontSize: '.75rem', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                Esta opcion de pago ya no esta disponible — la fecha limite ha pasado
              </div>
            )}
            {!singleExpired && selectedPlan === 'single' && (
              <div style={{ marginTop: '.6rem', fontSize: '.75rem', color: 'var(--sage)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                Seleccionado — seras redirigido al pago al confirmar
              </div>
            )}
          </button>

          {/* Payment options (multi-payment) */}
          {event.paymentOptions.map(opt => {
            const isSelected = selectedPlan === opt.id;
            return (
              <button key={opt.id}
                onClick={() => onSelect(opt.id)}
                style={{
                  width: '100%', textAlign: 'left', padding: '1rem 1.1rem',
                  border: `2px solid ${isSelected ? 'var(--sage)' : 'rgba(122,171,138,.2)'}`,
                  borderRadius: 'var(--radius)',
                  background: isSelected ? 'rgba(122,171,138,.12)' : 'rgba(255,255,255,.03)',
                  cursor: 'pointer',
                  transition: 'all .2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>{opt.label}</div>
                    <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.2rem' }}>Paga en {opt.numPayments} veces</div>

                    <div style={{ marginTop: '.5rem', display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                      {/* Senial line */}
                      <div style={{ fontSize: '.75rem', color: 'var(--amber)' }}>
                        Senial: {depositPerPerson.toFixed(2)} EUR/persona (al inscribirte)
                      </div>
                      {/* Installment lines */}
                      {opt.plans.map((p, i) => (
                        <div key={i} style={{ fontSize: '.75rem', color: 'var(--sage)' }}>
                          {p.label}: {(parseFloat(p.amount) || 0).toFixed(2)} EUR/persona
                          {p.dueDate && ` — antes del ${new Date(p.dueDate).toLocaleDateString('es-ES')}`}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.2rem', fontWeight: 700, color: isSelected ? 'var(--sage)' : 'var(--white)' }}>
                      {totalAmount.toFixed(2)} EUR
                    </div>
                    <div style={{ fontSize: '.65rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Total</div>
                  </div>
                </div>
                {isSelected && (
                  <div style={{ marginTop: '.6rem', fontSize: '.75rem', color: 'var(--sage)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                    Seleccionado — pagaras la senial de {(depositTotal).toFixed(2)} EUR al confirmar
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: '.8rem', padding: '.7rem 1rem', background: 'rgba(232,164,48,.08)', border: '1px solid rgba(232,164,48,.2)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--amber)' }}>
          Al confirmar seras redirigido a la pasarela de pago segura. La reserva se confirmara una vez completado el pago.
        </div>
      </div>
    );
  }

  // ── LEGACY FORMAT: fallback for events without paymentOptions ──
  const depositAmount = depositPct > 0 ? totalAmount * (depositPct / 100) : 0;
  const options = [];

  if (event.allowSinglePayment !== false) {
    options.push({
      id: 'single',
      icon: '',
      title: 'Pago unico',
      desc: 'Paga el importe completo ahora',
      amount: totalAmount,
      tag: 'Total',
      highlight: true,
    });
  }

  if (event.allowInstallments && event.paymentPlans?.length > 0) {
    if (depositPct > 0) {
      options.push({
        id: 'deposit',
        icon: '',
        title: `Senial (${depositPct}%) + plazos`,
        desc: `Reserva tu plaza ahora con la senial y paga el resto en plazos`,
        amount: depositAmount,
        tag: 'Senial',
        plans: event.paymentPlans,
      });
    } else {
      options.push({
        id: event.paymentPlans[0].id,
        icon: '',
        title: 'Pago a plazos',
        desc: 'Paga en varios plazos segun las fechas establecidas',
        amount: event.paymentPlans[0].amount * (members?.length || 1),
        tag: '1er plazo',
        plans: event.paymentPlans,
      });
    }
  }

  if (depositPct > 0 && !event.allowInstallments) {
    options.push({
      id: 'deposit',
      icon: '',
      title: `Senial (${depositPct}%)`,
      desc: 'Paga la senial ahora para reservar tu plaza',
      amount: depositAmount,
      tag: 'Senial',
    });
  }

  if (options.length === 0) return null;

  const isPlanExpired = (opt) => {
    if (!opt.dueDate) return false;
    return new Date(opt.dueDate) < now;
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div className="section-label" style={{ marginBottom: '.8rem' }}>Selecciona como quieres pagar</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        {options.map(opt => {
          const expired = isPlanExpired(opt);
          return (
          <button key={opt.id}
            onClick={() => !expired && onSelect(opt.id)}
            disabled={expired}
            style={{
              width: '100%', textAlign: 'left', padding: '1rem 1.1rem',
              border: `2px solid ${expired ? 'rgba(214,60,42,.3)' : selectedPlan === opt.id ? 'var(--sage)' : 'rgba(122,171,138,.2)'}`,
              borderRadius: 'var(--radius)',
              background: expired ? 'rgba(214,60,42,.06)' : selectedPlan === opt.id ? 'rgba(122,171,138,.12)' : 'rgba(255,255,255,.03)',
              cursor: expired ? 'not-allowed' : 'pointer',
              opacity: expired ? 0.75 : 1,
              transition: 'all .2s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '.7rem', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>{opt.title}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.2rem' }}>{opt.desc}</div>
                  {opt.plans && (
                    <div style={{ marginTop: '.5rem', display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                      {opt.plans.map((p, i) => (
                        <div key={i} style={{ fontSize: '.75rem', color: 'var(--sage)' }}>
                          {p.label}: {p.amount} EUR/persona
                          {p.dueDate && ` — antes del ${new Date(p.dueDate).toLocaleDateString('es-ES')}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.2rem', fontWeight: 700, color: selectedPlan === opt.id ? 'var(--sage)' : 'var(--white)' }}>
                  {opt.amount.toFixed(2)} EUR
                </div>
                <div style={{ fontSize: '.65rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{opt.tag}</div>
              </div>
            </div>
            {expired && (
              <div style={{ marginTop: '.6rem', fontSize: '.75rem', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                Esta opcion de pago ya no esta disponible — la fecha limite ha pasado
              </div>
            )}
            {!expired && selectedPlan === opt.id && (
              <div style={{ marginTop: '.6rem', fontSize: '.75rem', color: 'var(--sage)', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                Seleccionado — seras redirigido al pago al confirmar
              </div>
            )}
          </button>
          );
        })}
      </div>

      <div style={{ marginTop: '.8rem', padding: '.7rem 1rem', background: 'rgba(232,164,48,.08)', border: '1px solid rgba(232,164,48,.2)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--amber)' }}>
        Al confirmar seras redirigido a la pasarela de pago segura. La reserva se confirmara una vez completado el pago.
      </div>
    </div>
  );
}

// ── Modal principal ──────────────────────────────────────────
export default function BookingModal({ event, onClose, onSuccess }) {
  const { token } = useAuth();
  const isGroup = event?.registrationType === 'group';
  const requiresPayment = !!event?.requiresPayment;
  // Fechas flexibles: la familia elige llegada/salida dentro del periodo del evento.
  // Sin fechas: el evento no define fechas y la familia elige llegada/salida libremente.
  const isFlexible = !!event?.fechasFlexibles;
  const sinFechas = !!event?.sinFechas;
  const needsStay = isFlexible || sinFechas;
  const todayStr = new Date().toISOString().slice(0, 10);
  const windowMin = event?.date ? String(event.date).slice(0, 10) : todayStr;
  const windowMax = event?.dateEnd ? String(event.dateEnd).slice(0, 10) : '';
  // Horas de entrada/salida definidas por el organizador ('HH:mm')
  const horarioStay = (event?.checkInTime || event?.checkOutTime)
    ? [event?.checkInTime && `entrada ${event.checkInTime}`, event?.checkOutTime && `salida ${event.checkOutTime}`].filter(Boolean).join(' · ')
    : '';

  const [step, setStep] = useState('form'); // 'form' | 'payment'
  const [stay, setStay] = useState({ checkInDate: '', checkOutDate: '' });
  const [members, setMembers] = useState([emptyPerson()]);
  const [groupData, setGroupData] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(() => {
    if (!requiresPayment) return null;
    // New format: default to single
    if (Array.isArray(event?.paymentOptions) && event.paymentOptions.length > 0) return 'single';
    // Legacy format
    if (event?.allowSinglePayment !== false) return 'single';
    if (event?.depositPercent > 0) return 'deposit';
    if (event?.allowInstallments && event?.paymentPlans?.length > 0) return event.paymentPlans[0].id;
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [packList, setPackList] = useState([]);
  const [personas, setPersonas] = useState([]);
  // Cuando el evento NO requiere senial, tras guardar la reserva preguntamos
  // al usuario si quiere hacer el primer pago (o el pago completo si eligio 'single') ahora.
  const [paymentPrompt, setPaymentPrompt] = useState(null); // { bookingId, planForCheckout } | null
  // Para eventos de colegio: cuando ya hay reserva previa, preguntar antes de crear otra.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null); // { existingCount } | null

  useEffect(() => {
    api.request('GET', '/api/admin/question-packs', null, token)
      .then(d => setPackList(d.packs || []))
      .catch(() => {});
    // Cargar personas guardadas del usuario
    api.getMyPersonas(token)
      .then(d => setPersonas(d.personas || []))
      .catch(() => {});
  }, [token]);

  const updateMember = (i, val) => setMembers(m => m.map((x, idx) => idx === i ? val : x));
  const addMember = () => setMembers(m => [...m, emptyPerson()]);
  const removeMember = (i) => setMembers(m => m.filter((_, idx) => idx !== i));

  // Seleccionar persona guardada para un miembro
  const selectPersona = (memberIndex, personaId) => {
    if (!personaId) {
      // Deseleccionar: limpiar formulario
      updateMember(memberIndex, emptyPerson());
      return;
    }
    const persona = personas.find(p => p.id === personaId);
    if (persona) {
      const memberData = personaToMember(persona);
      // Preservar customFields existentes
      const current = members[memberIndex];
      memberData.customFields = current?.customFields || {};
      updateMember(memberIndex, memberData);
    }
  };

  // Personas ya usadas por otros miembros (para filtrar)
  const usedPersonaIds = members.map(m => m._personaId).filter(Boolean);

  const handleSubmit = async (confirmDuplicate = false) => {
    setError(''); setLoading(true);
    // Eventos flexibles o sin fechas: llegada y salida obligatorias y coherentes
    if (needsStay) {
      if (!stay.checkInDate || !stay.checkOutDate) {
        setError('Indica tu fecha de llegada y de salida.');
        setLoading(false);
        if (step === 'payment') setStep('form');
        return;
      }
      if (stay.checkOutDate <= stay.checkInDate) {
        setError('La fecha de salida debe ser posterior a la de llegada.');
        setLoading(false);
        if (step === 'payment') setStep('form');
        return;
      }
    }
    // Validar cesión de datos obligatoria
    const missingCesion = members.findIndex(m => !m.autorizaCesionDatos);
    if (missingCesion !== -1) {
      setError(`La persona ${missingCesion + 1} debe autorizar la cesión de datos personales para continuar.`);
      setLoading(false);
      if (step === 'payment') setStep('form');
      return;
    }
    try {
      const res = await api.createBooking(token, {
        eventId: event.id,
        eventPassword: event._eventPassword || undefined,
        checkInDate: needsStay ? stay.checkInDate : undefined,
        checkOutDate: needsStay ? stay.checkOutDate : undefined,
        familyFormData: groupData,
        confirmDuplicate: confirmDuplicate || undefined,
        selectedPlan: requiresPayment ? selectedPlan : null,
        members: members.map(m => {
          const birthYear = parseInt(m.birthYear) || null;
          const age = birthYear ? new Date().getFullYear() - birthYear : null;
          const role = age !== null && age < 18 ? 'child' : 'adult';
          return {
            name: `${m.firstName} ${m.lastName1} ${m.lastName2}`.trim(),
            firstName: m.firstName, lastName1: m.lastName1, lastName2: m.lastName2,
            birthYear, age, role, sex: m.sex,
            email: m.email, phoneExt: m.phoneExt, phone: m.phone,
            tutorName: m.tutorName, address: m.address,
            municipality: m.municipality, province: m.province,
            country: m.country, docType: m.docType, docNum: m.docNum,
            docSupport: m.docSupport, docEmission: m.docEmission,
            // Salud
            alergias: m.alergias || '', medicacionActual: m.medicacionActual || '',
            enfermedades: m.enfermedades || '', grupoSanguineo: m.grupoSanguineo || '',
            tarjetaSanitaria: m.tarjetaSanitaria || '',
            contactoEmergenciaNombre: m.contactoEmergenciaNombre || '',
            contactoEmergenciaTelefono: m.contactoEmergenciaTelefono || '',
            contactoEmergenciaExt: m.contactoEmergenciaExt || '+34',
            contactoEmergenciaRelacion: m.contactoEmergenciaRelacion || '',
            autorizaMedicacion: !!m.autorizaMedicacion,
            autorizaCesionDatos: !!m.autorizaCesionDatos,
            autorizaImagenRedes: !!m.autorizaImagenRedes,
            autorizaComunicaciones: !!m.autorizaComunicaciones,
            customFields: m.customFields || {},
            personaId: m._personaId || null,
          };
        }),
      });

      // Si hay pago, gestionar el flujo:
      //  - Si el evento requiere senial: redirigir a Stripe inmediatamente (flujo actual)
      //  - Si NO requiere senial: mostrar ventana que pregunta si quiere pagar ahora
      if (requiresPayment && selectedPlan && res.bookingId) {
        if (event.depositRequired) {
          // Flujo existente: ir a Stripe ya
          const checkoutPlanId = selectedPlan.startsWith('opt_') ? 'deposit' : selectedPlan;
          const checkout = await api.request('POST',
            `/api/payments/bookings/${res.bookingId}/checkout/${checkoutPlanId}`,
            {}, token);
          if (checkout.url) {
            window.location.href = checkout.url;
            return;
          }
        } else {
          // Sin senial: determinar el primer pago a ofrecer
          //  - 'single' → pago completo
          //  - 'opt_xxx' → primer plazo de la opcion
          //  - legacy 'plan_xxx' → ese mismo plazo
          let planForCheckout = selectedPlan;
          if (selectedPlan.startsWith('opt_')) {
            const opt = (event.paymentOptions || []).find(o => o.id === selectedPlan);
            const firstPlan = opt?.plans?.[0];
            planForCheckout = firstPlan?.id || null;
          }
          setPaymentPrompt({ bookingId: res.bookingId, planForCheckout });
          setLoading(false);
          return;
        }
      }

      onSuccess();
    } catch (err) {
      // Evento de colegio con reserva previa: preguntar al usuario si quiere continuar
      if (err?.code === 'DUPLICATE_BOOKING_ALLOWED') {
        setDuplicatePrompt({ existingCount: err.data?.existingCount ?? 1 });
        setLoading(false);
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calcTotal = () => {
    if (!event?.useAgePricing || !event?.agePricing) {
      return (event?.pricePerPerson || 0) * members.length;
    }
    return members.reduce((sum, m) => {
      const birthYear = parseInt(m.birthYear);
      // If birthYear is empty/invalid, default to adult price (not free)
      if (!birthYear) return sum + (parseFloat(event.agePricing.adult?.price) || 0);
      const age = new Date().getFullYear() - birthYear;
      if (age <= 3) return sum;
      if (age <= 15) return sum + (parseFloat(event.agePricing.child?.price) || 0);
      return sum + (parseFloat(event.agePricing.adult?.price) || 0);
    }, 0);
  };

  const calcDeposit = () => calcTotal() * ((event?.depositPercent || 20) / 100);

  const familyFields = (event?.formSchema || []).filter(f => f.scope === 'family');
  const memberFields = (event?.formSchema || []).filter(f => f.scope === 'member' || !f.scope);

  const canProceedToPayment = requiresPayment &&
    (Array.isArray(event?.paymentOptions) && event.paymentOptions.length > 0 ||
     event?.allowSinglePayment !== false ||
     event?.depositPercent > 0 ||
     (event?.allowInstallments && event?.paymentPlans?.length > 0));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="section-label">
              {step === 'form' ? 'Inscribirse' : 'Confirmar pago'}
            </div>
            <h2>{event?.name}</h2>
            <p style={{ fontSize: '.85rem', color: 'var(--mid)', marginTop: '.2rem' }}>
              {sinFechas
                ? 'Tú eliges las fechas de tu estancia — las indicas abajo'
                : isFlexible && event?.dateEnd
                  ? `Del ${new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} al ${new Date(event.dateEnd).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} — eliges tus fechas abajo`
                  : new Date(event?.date).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              {event?.location && ` · ${event.location}`}
            </p>
            {horarioStay && (
              <p style={{ fontSize: '.8rem', color: 'var(--sage)', marginTop: '.15rem' }}>
                🕓 Horario: {horarioStay}
              </p>
            )}
            {requiresPayment && (
              <div style={{ marginTop: '.4rem' }}>
                {event.useAgePricing ? (
                  <span className="badge badge-amber">💳 👶 0€ · 🧒 {parseFloat(event.agePricing?.child?.price || 0).toFixed(2)}€ · 🧑 {parseFloat(event.agePricing?.adult?.price || 0).toFixed(2)}€</span>
                ) : (
                  <span className="badge badge-amber">💳 {parseFloat(event.pricePerPerson).toFixed(2)} € / persona</span>
                )}
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Indicador de pasos si hay pago */}
        {canProceedToPayment && (
          <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1.2rem' }}>
            {['form', 'payment'].map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                {i > 0 && <div style={{ width: 24, height: 1, background: 'rgba(122,171,138,.2)' }} />}
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '.65rem', fontWeight: 700,
                  background: step === s ? 'var(--fern)' : step === 'payment' && s === 'form' ? 'rgba(122,171,138,.3)' : 'rgba(255,255,255,.08)',
                  color: step === s ? 'var(--forest)' : 'var(--mid)',
                }}>
                  {step === 'payment' && s === 'form' ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: '.75rem', color: step === s ? 'var(--white)' : 'var(--mid)', fontWeight: step === s ? 600 : 400 }}>
                  {s === 'form' ? 'Datos' : 'Pago'}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

        {/* PASO 1: Formulario */}
        {step === 'form' && (
          <>
            {needsStay && (
              <div style={{ background: 'rgba(122,171,138,.07)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
                <div style={{ padding: '.7rem 1rem', borderBottom: '1px solid rgba(122,171,138,.15)' }}>
                  <div style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.12em', color: 'var(--sage)', textTransform: 'uppercase' }}>🗓 ¿Cuándo venís?</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.1rem' }}>
                    {sinFechas
                      ? 'Elige tu fecha de llegada y de salida'
                      : <>Elige tus fechas dentro del periodo
                        {event?.date && windowMax && ` (${new Date(event.date).toLocaleDateString('es-ES')} – ${new Date(event.dateEnd).toLocaleDateString('es-ES')})`}</>}
                    {horarioStay && ` · 🕓 ${horarioStay}`}
                  </div>
                </div>
                <div style={{ padding: '.9rem 1rem', background: 'rgba(255,255,255,.03)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.8rem' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Fecha de llegada <span className="req">*</span></label>
                    <input className="form-input" type="date"
                      min={windowMin} max={windowMax || undefined}
                      value={stay.checkInDate}
                      onChange={e => setStay(s => ({ ...s, checkInDate: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Fecha de salida <span className="req">*</span></label>
                    <input className="form-input" type="date"
                      min={stay.checkInDate || windowMin} max={windowMax || undefined}
                      value={stay.checkOutDate}
                      onChange={e => setStay(s => ({ ...s, checkOutDate: e.target.value }))} />
                  </div>
                  {stay.checkInDate && stay.checkOutDate && stay.checkOutDate > stay.checkInDate && (
                    <div style={{ gridColumn: '1 / -1', fontSize: '.8rem', color: 'var(--sage)' }}>
                      {Math.round((new Date(stay.checkOutDate) - new Date(stay.checkInDate)) / 86400000)} noche{Math.round((new Date(stay.checkOutDate) - new Date(stay.checkInDate)) / 86400000) !== 1 ? 's' : ''} · llegada {new Date(stay.checkInDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} → salida {new Date(stay.checkOutDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isGroup && familyFields.length > 0 && (
              <div style={{ background: 'rgba(122,171,138,.07)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
                <div style={{ padding: '.7rem 1rem', borderBottom: '1px solid rgba(122,171,138,.15)' }}>
                  <div style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.12em', color: 'var(--sage)', textTransform: 'uppercase' }}>◈ Datos del grupo</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.1rem' }}>Se rellenan una vez para toda la inscripción</div>
                </div>
                <div style={{ padding: '.9rem 1rem', background: 'rgba(255,255,255,.03)' }}>
                  {familyFields.map(field => (
                    <div className="form-group" key={field.id}>
                      <label className="form-label">{field.label} {field.required && <span className="req">*</span>}</label>
                      {field.type === 'boolean' ? (
                        <label className="toggle-wrap">
                          <input type="checkbox" checked={!!groupData[field.id]} onChange={e => setGroupData(p => ({ ...p, [field.id]: e.target.checked }))} />
                          <span className="toggle-track"><span className="toggle-thumb" /></span>
                          <span style={{ fontSize: '.9rem', color: 'var(--mid)' }}>{groupData[field.id] ? 'Sí' : 'No'}</span>
                        </label>
                      ) : (
                        <input className="form-input" value={groupData[field.id] || ''} onChange={e => setGroupData(p => ({ ...p, [field.id]: e.target.value }))} placeholder={field.placeholder} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.8rem' }}>
              <div className="section-label" style={{ flex: 1, marginBottom: 0 }}>
                ⚔ {isGroup ? `Personas a inscribir (${members.length})` : 'Tus datos'}
              </div>
              {isGroup && <button className="btn btn-ghost btn-sm" onClick={addMember}>+ Añadir persona</button>}
            </div>

            {members.map((member, i) => {
              // Personas disponibles: no usadas por otros miembros
              const availablePersonas = personas.filter(p =>
                p.id === member._personaId || !usedPersonaIds.includes(p.id) || member._personaId === p.id
              );
              return (
              <div className="member-box" key={i}>
                {isGroup && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.7rem' }}>
                    <div className="member-label">Persona {i + 1}</div>
                    {i > 0 && <button className="btn btn-danger btn-sm" style={{ padding: '.2rem .5rem' }} onClick={() => removeMember(i)}>✕</button>}
                  </div>
                )}
                <PersonaSelector
                  personas={availablePersonas}
                  selectedId={member._personaId}
                  onSelect={(personaId) => selectPersona(i, personaId)}
                />
                <DynamicForm fields={memberFields} value={member} onChange={val => updateMember(i, val)} prefix={`member-${i}`} packs={packList} token={token} excludedMandatory={event?.excludedMandatory || []} />
              </div>
              );
            })}

            <div style={{ display: 'flex', gap: '.8rem', marginTop: '1.2rem' }}>
              <button className="btn btn-ghost btn-full" onClick={onClose}>Cancelar</button>
              {canProceedToPayment ? (
                <button className="btn btn-primary btn-full" onClick={() => {
                  if (needsStay && (!stay.checkInDate || !stay.checkOutDate)) {
                    setError('Indica tu fecha de llegada y de salida.'); return;
                  }
                  if (needsStay && stay.checkOutDate <= stay.checkInDate) {
                    setError('La fecha de salida debe ser posterior a la de llegada.'); return;
                  }
                  setError(''); setStep('payment');
                }}>
                  Siguiente: Pago →
                </button>
              ) : (
                <button className="btn btn-primary btn-full" onClick={() => handleSubmit(false)} disabled={loading}>
                  {loading ? <><span className="spinner" /> Enviando...</> : 'Confirmar inscripción'}
                </button>
              )}
            </div>
          </>
        )}

        {/* PASO 2: Pago */}
        {step === 'payment' && (
          <>
            <PaymentSelector event={event} selectedPlan={selectedPlan} onSelect={setSelectedPlan} members={members} />

            <div style={{ display: 'flex', gap: '.8rem', marginTop: '1.2rem' }}>
              <button className="btn btn-ghost btn-full" onClick={() => setStep('form')}>← Volver</button>
              <button className="btn btn-primary btn-full" onClick={() => handleSubmit(false)} disabled={loading || !selectedPlan}>
                {loading
                  ? <><span className="spinner" /> Procesando...</>
                  : (event.depositRequired ? 'Confirmar y pagar' : 'Guardar')}
              </button>
            </div>
          </>
        )}

        {paymentPrompt && (
          <PaymentPromptModal
            event={event}
            selectedPlan={selectedPlan}
            paymentPrompt={paymentPrompt}
            token={token}
            onClose={() => { setPaymentPrompt(null); onSuccess(); }}
          />
        )}

        {duplicatePrompt && (
          <DuplicateBookingModal
            existingCount={duplicatePrompt.existingCount}
            loading={loading}
            onCancel={() => setDuplicatePrompt(null)}
            onConfirm={() => {
              setDuplicatePrompt(null);
              handleSubmit(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Modal: confirmar segunda reserva para eventos de colegio ─────────
function DuplicateBookingModal({ existingCount, loading, onCancel, onConfirm }) {
  const textoReservas = existingCount === 1 ? 'una reserva' : `${existingCount} reservas`;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !loading && onCancel()} style={{ zIndex: 1001 }}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div>
            <div className="section-label">Aviso</div>
            <h2 style={{ fontSize: '1.05rem' }}>Ya tienes {textoReservas} para este evento</h2>
          </div>
        </div>
        <p style={{ fontSize: '.88rem', color: 'var(--mid)', marginTop: '.4rem', marginBottom: '1rem' }}>
          Si vas a inscribir a otro hijo o persona distinta puedes crear una segunda reserva.
          ¿Seguro que quieres realizar otra reserva adicional?
        </p>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <button className="btn btn-ghost btn-full" onClick={onCancel} disabled={loading}>
            No, cancelar
          </button>
          <button className="btn btn-primary btn-full" onClick={onConfirm} disabled={loading}>
            {loading ? <><span className="spinner" /> Procesando...</> : 'Si, crear otra reserva'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: preguntar si quiere pagar ahora (cuando no hay senial) ─────
function PaymentPromptModal({ event, selectedPlan, paymentPrompt, token, onClose }) {
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState('');

  const isSingle = selectedPlan === 'single';
  const title = isSingle ? '¿Realizar el pago completo ahora?' : '¿Realizar el primer pago ahora?';
  const descripcion = isSingle
    ? 'Puedes hacer el pago integro ahora, o guardar la reserva y pagarla desde "Mis reservas" en las proximas 48 horas (despues la plaza se libera).'
    : 'Puedes hacer el primer pago ahora, o guardar la reserva y pagarlo desde "Mis reservas" en las proximas 48 horas (despues la plaza se libera).';

  const handlePayNow = async () => {
    if (!paymentPrompt?.planForCheckout) {
      setErr('No se ha podido determinar el plan a pagar.');
      return;
    }
    setProcessing(true);
    setErr('');
    try {
      const checkout = await api.request(
        'POST',
        `/api/payments/bookings/${paymentPrompt.bookingId}/checkout/${paymentPrompt.planForCheckout}`,
        {},
        token,
      );
      if (checkout.url) {
        window.location.href = checkout.url;
        return;
      }
      setErr('No se ha podido iniciar el pago.');
    } catch (e) {
      setErr(e.message || 'No se ha podido iniciar el pago.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !processing && onClose()} style={{ zIndex: 1001 }}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div>
            <div className="section-label">Reserva guardada</div>
            <h2 style={{ fontSize: '1.1rem' }}>{title}</h2>
          </div>
        </div>
        <p style={{ fontSize: '.88rem', color: 'var(--mid)', marginTop: '.4rem', marginBottom: '1rem' }}>
          {descripcion}
        </p>
        {err && <div className="form-error" style={{ marginBottom: '.6rem' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <button className="btn btn-ghost btn-full" onClick={onClose} disabled={processing}>
            Guardar sin pagar
          </button>
          <button className="btn btn-primary btn-full" onClick={handlePayNow} disabled={processing}>
            {processing ? <><span className="spinner" /> Procesando...</> : 'Pagar ahora'}
          </button>
        </div>
      </div>
    </div>
  );
}
